import { useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { CalendarRange, Eye, Plus, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { PageHeader } from '@/components/ui/page-header';
import { DataTable } from '@/components/data-table/DataTable';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { useTiers } from '@/hooks/queries/useTiers';
import { useEcheances } from '@/hooks/queries/useEcheances';
import { useDeletePlanPaiement, usePlansPaiement } from '@/hooks/queries/usePlansPaiement';
import { formatDA, formatDateDZ } from '@/lib/format';
import { cn } from '@/lib/utils';
import { t } from '@/locales/i18n';
import type {
  EcheanceRow,
  PlanPaiementFrequence,
  PlanPaiementRow,
  PlanPaiementStatut,
} from '@/lib/database.types';
import { PlanPaiementFormDialog } from './PlanPaiementFormDialog';

const STATUT_VARIANT: Record<
  PlanPaiementStatut,
  'default' | 'success' | 'warning' | 'destructive' | 'outline' | 'secondary'
> = {
  actif: 'default',
  respecte: 'success',
  en_retard: 'warning',
  cloture: 'secondary',
  annule: 'destructive',
};

export function PlansPaiementPage(): JSX.Element {
  const { data: entreprises = [] } = useEntreprises();
  const [entrepriseId, setEntrepriseId] = useState('');
  useEffect(() => {
    if (!entrepriseId && entreprises[0]) setEntrepriseId(entreprises[0].id);
  }, [entreprises, entrepriseId]);

  const { data: plans = [], isLoading } = usePlansPaiement({ entrepriseId });
  const { data: tiers = [] } = useTiers({ entrepriseId, search: '' });
  const tierMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const tt of tiers) m.set(tt.id, tt.raison_sociale);
    return m;
  }, [tiers]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PlanPaiementRow | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<PlanPaiementRow | null>(null);
  const deletePlan = useDeletePlanPaiement();

  const columns = useMemo<ColumnDef<PlanPaiementRow>[]>(
    () => [
      {
        accessorKey: 'reference',
        header: 'Réf.',
        size: 130,
        cell: ({ getValue }) => <span className="font-mono text-xs">{String(getValue())}</span>,
      },
      {
        accessorKey: 'tier_id',
        header: 'Tiers',
        size: 220,
        cell: ({ getValue }) => (
          <span className="text-xs">{tierMap.get(getValue() as string) ?? '—'}</span>
        ),
      },
      {
        accessorKey: 'sens',
        header: 'Sens',
        size: 110,
        cell: ({ getValue }) => (
          <Badge variant={(getValue() as string) === 'a_encaisser' ? 'success' : 'warning'}>
            {(getValue() as string) === 'a_encaisser' ? 'À encaisser' : 'À payer'}
          </Badge>
        ),
      },
      {
        accessorKey: 'montant_total',
        header: 'Montant total',
        size: 130,
        cell: ({ getValue, row }) => (
          <span className="num text-xs">
            {formatDA(Number(getValue()))} {row.original.devise_code}
          </span>
        ),
      },
      {
        accessorKey: 'nb_echeances',
        header: 'Tranches',
        size: 80,
        cell: ({ getValue }) => <span className="num text-xs">{Number(getValue())}</span>,
      },
      {
        accessorKey: 'frequence',
        header: 'Fréquence',
        size: 130,
        cell: ({ getValue }) => (
          <span className="text-xs">
            {t(`echeances.plans.frequence.${getValue() as PlanPaiementFrequence}`)}
          </span>
        ),
      },
      {
        accessorKey: 'date_premiere',
        header: '1ère tranche',
        size: 120,
        cell: ({ getValue }) => (
          <span className="num text-xs">{formatDateDZ(String(getValue()))}</span>
        ),
      },
      {
        accessorKey: 'statut',
        header: 'Statut',
        size: 110,
        cell: ({ getValue }) => {
          const s = getValue() as PlanPaiementStatut;
          return <Badge variant={STATUT_VARIANT[s]}>{t(`echeances.plans.statut.${s}`)}</Badge>;
        },
      },
      {
        id: 'actions',
        header: '',
        size: 140,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => setSelectedPlan(row.original)}
            >
              <Eye className="h-3 w-3" /> Tranches
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-destructive hover:text-destructive"
              onClick={() => setConfirmDelete(row.original)}
              aria-label="Supprimer"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ),
      },
    ],
    [tierMap],
  );

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title={t('echeances.plans.title')}
        subtitle={t('echeances.plans.subtitle')}
        actions={
          <div className="flex items-center gap-2">
            <select
              value={entrepriseId}
              onChange={(e) => setEntrepriseId(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              {entreprises.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nom}
                </option>
              ))}
            </select>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="h-4 w-4" />
              {t('echeances.plans.newPlan')}
            </Button>
          </div>
        }
      />

      <DataTable
        data={plans}
        columns={columns}
        loading={isLoading}
        pageSize={50}
        searchPlaceholder="Rechercher…"
        exportFileName="plans-paiement.csv"
      />

      <PlanPaiementFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        entrepriseId={entrepriseId}
        onSaved={() => setDialogOpen(false)}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        destructive
        title={`Supprimer le plan « ${confirmDelete?.reference} » ?`}
        description="Toutes les tranches liées seront détachées du plan (mais pas supprimées)."
        loading={deletePlan.isPending}
        onConfirm={async () => {
          if (!confirmDelete) return;
          await deletePlan.mutateAsync(confirmDelete.id);
          toast.success('Plan supprimé');
          setConfirmDelete(null);
        }}
      />

      <PlanTranchesPanel
        plan={selectedPlan}
        onClose={() => setSelectedPlan(null)}
        tierName={selectedPlan ? (tierMap.get(selectedPlan.tier_id) ?? '') : ''}
      />
    </div>
  );
}

function PlanTranchesPanel({
  plan,
  tierName,
  onClose,
}: {
  plan: PlanPaiementRow | null;
  tierName: string;
  onClose: () => void;
}): JSX.Element | null {
  const { data } = useEcheances({
    entrepriseId: plan?.entreprise_id ?? '',
    planPaiementId: plan?.id ?? '',
    pageSize: 200,
  });
  if (!plan) return null;
  const tranches = (data?.rows ?? [])
    .slice()
    .sort((a, b) => (a.tranche_numero ?? 0) - (b.tranche_numero ?? 0));
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-end bg-black/30">
      <div className="flex h-full w-full max-w-xl flex-col bg-background shadow-xl">
        <div className="flex items-center justify-between border-b border-border p-4">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold">
              <CalendarRange className="h-4 w-4" />
              {plan.reference} — {tierName}
            </div>
            <div className="text-xs text-muted-foreground">
              {plan.nb_echeances} tranches · {formatDA(Number(plan.montant_total))}{' '}
              {plan.devise_code}
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={onClose}>
            Fermer
          </Button>
        </div>
        <div className="scrollbar-thin flex-1 overflow-y-auto p-4">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="py-2">#</th>
                <th>Échéance</th>
                <th>Montant</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {tranches.map((tr: EcheanceRow) => (
                <tr key={tr.id} className="border-b border-border/40">
                  <td className="py-2 font-mono">{tr.tranche_numero}</td>
                  <td className={cn('num', tr.statut === 'en_retard' && 'text-destructive')}>
                    {formatDateDZ(tr.date_echeance)}
                  </td>
                  <td className="num">{formatDA(Number(tr.montant_initial))}</td>
                  <td>
                    <Badge
                      variant={
                        tr.statut === 'payee'
                          ? 'success'
                          : tr.statut === 'en_retard'
                            ? 'destructive'
                            : 'outline'
                      }
                    >
                      {t(`echeances.statut.${tr.statut}`)}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
